import { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetCalendar } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";

/*
  useGetCalendarFromParams
  - Reads :handle and :calendarId from route params
  - Initializes calendar from location.state?.calendar if present
  - If calendar is null, fetches it via useGetCalendar and stores it in local state
  - Returns { calendar, isLoadingCalendar, isCalendarError }
*/
export const useGetCalendarFromParams = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { handle, calendarId } = useParams();

  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  // Initialize from location.state if available
  const [calendar, setCalendar] = useState(location.state?.calendar ?? null);
  // Keep calendar in sync if navigation state provides it later
  useEffect(() => {
    if (location.state?.calendar) {
      setCalendar(location.state.calendar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.calendar]);

  // Fetch when missing
  const { isFetching: isLoadingCalendar, isError: isCalendarError } =
    useGetCalendar(
      { handle, calendarId },
      {
        enabled: !!user && !!handle && !!calendarId && !calendar,
        onSuccess: (data) => {
          if (!data) return;
          setCalendar(data);
        },
        onError: (error) => {
          // Keep behavior consistent across pages without exposing error state
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            "An error occurred loading the calendar.",
            "error"
          );
          // eslint-disable-next-line no-console
          console.error("Error fetching calendar:", error);
        },
      }
    );

  return { calendar, isLoadingCalendar, isCalendarError };
};

export default useGetCalendarFromParams;
